import { useState, useCallback, useMemo } from 'react';
import { Check, FlaskConical, Droplets } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import SolubilityGrid, { SOLUBILITY_DATA, CATIONS } from './SolubilityGrid';

interface AnionTestingPartProps {
  partNumber: number;
  anionSymbol: string;
  anionName: string;
  anionFormula: string;
  anionColor: string;
  infoContent: {
    title: string;
    content: string;
    equation?: string;
  };
}

export default function AnionTestingPart({
  partNumber,
  anionSymbol,
  anionName,
  anionFormula,
  anionColor,
  infoContent
}: AnionTestingPartProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [testedCations, setTestedCations] = useState<Set<string>>(new Set());
  const [currentCation, setCurrentCation] = useState<string | null>(null);
  const [resultsRecorded, setResultsRecorded] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const completedSteps = testedCations.size === CATIONS.length && resultsRecorded ? 2 : testedCations.size > 0 ? 1 : 0;

  const handleTestCation = useCallback((cation: string) => {
    if (!testedCations.has(cation)) {
      setTestedCations(prev => {
        const newSet = new Set(prev);
        newSet.add(cation);
        if (newSet.size === CATIONS.length) {
          setCurrentStep(2);
        }
        return newSet;
      });
      setCurrentCation(cation);
    }
  }, [testedCations]);

  const handleRecordResults = useCallback(() => {
    if (testedCations.size === CATIONS.length && !resultsRecorded) {
      setResultsRecorded(true);
    }
  }, [testedCations.size, resultsRecorded]);

  // Get precipitates formed for this anion
  const precipitatesFormed = useMemo(() => {
    const precipitates: { cation: string; precipitate: string; color: string; description: string }[] = [];
    CATIONS.forEach(cation => {
      const data = SOLUBILITY_DATA[cation]?.[anionSymbol];
      if (data && !data.soluble && data.precipitate) {
        precipitates.push({
          cation,
          precipitate: data.precipitate,
          color: data.color || '#FFFFFF',
          description: data.description || ''
        });
      }
    });
    return precipitates;
  }, [anionSymbol]);

  // Get tested cell keys for the grid
  const testedCellKeys = useMemo(() => {
    const keys = new Set<string>();
    testedCations.forEach(cation => {
      keys.add(`${cation}-${anionSymbol}`);
    });
    return keys;
  }, [testedCations, anionSymbol]);

  // Get current test result
  const currentResult = currentCation ? SOLUBILITY_DATA[currentCation]?.[anionSymbol] : null;

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part {partNumber}: {anionName} Testing</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: `Test ${anionFormula}`, hint: `Test with all 11 cations`, done: testedCations.size === CATIONS.length },
            { id: 2, title: 'Record Results', hint: 'Note precipitate formation', done: resultsRecorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              <button
                className="step-info-btn"
                onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
              >
                ?
              </button>
            </div>
          ))}
        </div>

        {/* Info Panel */}
        <div className="info-panel">
          <h4>{infoContent.title}</h4>
          <p>{infoContent.content}</p>
          {infoContent.equation && (
            <div className="equation">{infoContent.equation}</div>
          )}
        </div>

        {/* Progress */}
        <div className="progress-panel">
          <div className="progress-label">Testing Progress</div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(testedCations.size / CATIONS.length) * 100}%` }}
            />
          </div>
          <div className="progress-text">{testedCations.size} / {CATIONS.length} cations tested</div>
        </div>
      </div>

      <LabBench
        experimentName={`${anionName} Solubility Testing`}
        completedCount={completedSteps}
        totalSteps={2}
        equipment={[`${anionFormula} solution`, 'Cation solutions', 'Acetate grid']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="testing-workspace">
          {/* Anion Reagent Bottle */}
          <div className="reagent-section">
            <div className="reagent-bottle">
              <svg viewBox="0 0 50 90" width={60} height={100}>
                <rect x="12" y="25" width="26" height="55" rx="3" fill="#5A5A5A" />
                <rect x="15" y="30" width="20" height="45" fill={anionColor} opacity="0.7" />
                <rect x="17" y="10" width="16" height="18" rx="2" fill="#333" />
                <text x="25" y="55" fontSize="7" fill="#333" textAnchor="middle" fontWeight="bold">
                  Na{anionSymbol.replace(/\d*[+-]/, '')}
                </text>
              </svg>
              <span className="bottle-label">{anionFormula} Solution</span>
            </div>
          </div>

          {/* Cation Testing Row */}
          <div className="cation-row">
            {CATIONS.map(cation => {
              const isTested = testedCations.has(cation);
              const result = SOLUBILITY_DATA[cation]?.[anionSymbol];
              const isPrecipitate = result && !result.soluble;

              return (
                <div
                  key={cation}
                  className={`cation-spot ${isTested ? 'tested' : 'untested'} ${!isTested ? 'clickable' : ''}`}
                  onClick={() => !isTested && handleTestCation(cation)}
                >
                  <svg viewBox="0 0 40 40" width={50} height={50}>
                    {/* Drop spot */}
                    <circle
                      cx="20"
                      cy="20"
                      r="16"
                      fill={isTested ? (isPrecipitate ? result?.color || '#DDD' : '#E8F5E9') : '#F5F5F5'}
                      stroke={isTested ? (isPrecipitate ? '#666' : 'var(--secondary)') : '#CCC'}
                      strokeWidth="2"
                    />

                    {/* Precipitate texture */}
                    {isTested && isPrecipitate && (
                      <g opacity="0.5">
                        <circle cx="15" cy="15" r="2" fill="rgba(0,0,0,0.2)" />
                        <circle cx="25" cy="18" r="1.5" fill="rgba(0,0,0,0.2)" />
                        <circle cx="18" cy="25" r="2" fill="rgba(0,0,0,0.2)" />
                        <circle cx="24" cy="24" r="1" fill="rgba(0,0,0,0.2)" />
                      </g>
                    )}

                    {/* Result indicator */}
                    {isTested && (
                      <text x="20" y="24" fontSize="10" fill={isPrecipitate ? '#333' : 'var(--secondary)'} textAnchor="middle" fontWeight="bold">
                        {isPrecipitate ? 'ppt' : 'S'}
                      </text>
                    )}
                  </svg>
                  <span className="cation-label">{cation}</span>
                </div>
              );
            })}
          </div>

          {/* Current Test Result */}
          {currentCation && currentResult && (
            <div className="current-result">
              <h4>{currentCation} + {anionSymbol}</h4>
              {currentResult.soluble ? (
                <p className="result-text soluble">No precipitate - ions remain in solution</p>
              ) : (
                <>
                  <p className="result-text insoluble">
                    Precipitate formed: <strong>{currentResult.precipitate}</strong>
                  </p>
                  {currentResult.description && (
                    <p className="result-description">{currentResult.description}</p>
                  )}
                  {currentResult.color && (
                    <div className="color-indicator" style={{ backgroundColor: currentResult.color }}>
                      Observed color
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {testedCations.size < CATIONS.length && (
              <div className="status-message info">
                <Droplets size={20} />
                Click each cation spot to add {anionFormula} and observe
              </div>
            )}
            {testedCations.size === CATIONS.length && !resultsRecorded && (
              <div className="status-message info" onClick={handleRecordResults} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click to record results: {precipitatesFormed.length} precipitates formed
              </div>
            )}
            {resultsRecorded && (
              <div className="status-message success">
                <Check size={20} />
                {anionName} testing complete! {precipitatesFormed.length} insoluble combinations found.
              </div>
            )}
          </div>

          {/* Results Summary */}
          {resultsRecorded && precipitatesFormed.length > 0 && (
            <div className="results-summary">
              <h4>Precipitates Formed with {anionFormula}</h4>
              <div className="precipitate-list">
                {precipitatesFormed.map(p => (
                  <div key={p.cation} className="precipitate-item">
                    <div className="color-dot" style={{ backgroundColor: p.color }} />
                    <span className="formula">{p.precipitate}</span>
                    <span className="desc">{p.description}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <style>{`
          .testing-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .reagent-section {
            display: flex;
            justify-content: center;
          }

          .reagent-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 12px;
            color: var(--text-secondary);
          }

          .cation-row {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            justify-content: center;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            padding: 20px;
            border-radius: 10px;
            max-width: 600px;
          }

          .cation-spot {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
            padding: 5px;
            border-radius: 6px;
            transition: all 0.2s;
          }

          .cation-spot.clickable {
            cursor: pointer;
          }

          .cation-spot.clickable:hover {
            background: rgba(255,255,255,0.1);
            transform: scale(1.1);
          }

          .cation-label {
            font-size: 9px;
            color: #CCC;
          }

          .current-result {
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 8px;
            text-align: center;
            min-width: 250px;
          }

          .current-result h4 {
            margin: 0 0 8px 0;
            color: var(--text-primary);
          }

          .result-text {
            margin: 0 0 8px 0;
            font-size: 14px;
          }

          .result-text.soluble {
            color: var(--secondary);
          }

          .result-text.insoluble {
            color: var(--text-primary);
          }

          .result-description {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
            font-style: italic;
          }

          .color-indicator {
            margin-top: 8px;
            padding: 8px 16px;
            border-radius: 4px;
            font-size: 11px;
            border: 1px solid var(--border);
          }

          .info-panel {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 12px;
            margin-top: 16px;
          }

          .info-panel h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--secondary);
          }

          .info-panel p {
            margin: 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .equation {
            background: var(--bg-surface);
            padding: 8px;
            border-radius: 4px;
            font-family: monospace;
            font-size: 11px;
            margin-top: 8px;
          }

          .progress-panel {
            background: var(--bg-surface);
            border-radius: 8px;
            padding: 12px;
            margin-top: 12px;
          }

          .progress-label {
            font-size: 11px;
            color: var(--text-secondary);
            margin-bottom: 6px;
          }

          .progress-bar {
            height: 8px;
            background: var(--border);
            border-radius: 4px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: var(--secondary);
            transition: width 0.3s;
          }

          .progress-text {
            font-size: 11px;
            color: var(--text-secondary);
            margin-top: 4px;
            text-align: right;
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .results-summary {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 400px;
            border-left: 3px solid var(--secondary);
          }

          .results-summary h4 {
            margin: 0 0 12px 0;
            font-size: 14px;
            color: var(--secondary);
          }

          .precipitate-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }

          .precipitate-item {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 13px;
          }

          .color-dot {
            width: 16px;
            height: 16px;
            border-radius: 3px;
            border: 1px solid var(--border);
          }

          .formula {
            font-weight: 600;
            min-width: 80px;
          }

          .desc {
            color: var(--text-secondary);
            font-size: 11px;
          }
        .step-info-btn {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: var(--accent);
          color: var(--bg-dark);
          font-size: 11px;
          font-weight: 700;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: transform 0.15s;
        }
        .step-info-btn:hover {
          transform: scale(1.1);
        }
        `}</style>
      </LabBench>

      {showInfo && (
        <InfoBox
          step={showInfo}
          experimentId="exp7"
          stepInfo={{
            title: showInfo === 1 ? infoContent.title : 'Recording Results',
            content: showInfo === 1 ? infoContent.content : `You have tested all 11 cations with ${anionName}. Record which combinations formed precipitates (marked "ppt") and which remained soluble (marked "S"). The Ksp (solubility product) determines whether a precipitate forms.`,
            equation: showInfo === 1 ? infoContent.equation : undefined,
            observe: showInfo === 1 ? undefined : [`${precipitatesFormed.length} precipitates formed`, 'Soluble combinations show clear solution', 'Precipitates show characteristic colors']
          }}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
